/**
 * Admin Custom events catalog: Braze context keys, worker-required keys, and UI field keys.
 * Brooklyn workers do not import this file; send shape is asserted in worker tests.
 */

export const LOGIN_CODE_EMAIL_MODE = {
  CODE_AND_LINK: 'code-and-link',
  LINK_ONLY: 'link-only',
} as const

export interface CustomEventTriggerCatalogEntry {
  eventName: string
  typicalBrazeContextKeys: readonly string[]
  adminFieldKeys: readonly string[]
  workerRequiredFieldKeys: readonly string[]
  notes?: string
}

/** Keys collected in Admin Custom events tab per template id */
export const CUSTOM_EVENT_TRIGGER_FIELD_CATALOG: Record<string, CustomEventTriggerCatalogEntry> = {
  PASSWORD_RESET: {
    eventName: 'PASSWORD_RESET',
    typicalBrazeContextKeys: [
      'event_name',
      'password_reset_url',
      'registration_language',
      'first_name',
    ],
    adminFieldKeys: ['password_reset_url', 'registration_language', 'first_name'],
    workerRequiredFieldKeys: ['password_reset_url'],
  },
  NEW_USER_PASSWORD_RESET: {
    eventName: 'NEW_USER_PASSWORD_RESET',
    typicalBrazeContextKeys: ['event_name', 'password_reset_url', 'registration_language'],
    adminFieldKeys: ['password_reset_url', 'registration_language'],
    workerRequiredFieldKeys: ['password_reset_url'],
    notes: 'Braze campaign trigger_properties (not Canvas context).',
  },
  LOGIN_CODE: {
    eventName: 'LOGIN_CODE',
    typicalBrazeContextKeys: [
      'event_name',
      'magic_link_url',
      'email_mode',
      'registration_language',
      'digits_code',
      'first_name',
    ],
    adminFieldKeys: [
      'magic_link_url',
      'email_mode',
      'web_app_server',
      'registration_language',
      'digits_code',
      'first_name',
    ],
    workerRequiredFieldKeys: ['magic_link_url'],
    notes: `digits_code required when email_mode is ${LOGIN_CODE_EMAIL_MODE.CODE_AND_LINK}.`,
  },
  GIFT_SUBSCRIPTION_RECIPIENT_DELIVERY: {
    eventName: 'GIFT_SUBSCRIPTION_RECIPIENT_DELIVERY',
    typicalBrazeContextKeys: [
      'event_name',
      'registration_language',
      'gifter_name',
      'recipient_first_name',
      'recipient_last_name',
      'delivery_date',
      'expires_at',
      'token_url',
      'token',
      'plan_name',
      'message',
    ],
    adminFieldKeys: [
      'gifterName',
      'recipientEmail',
      'recipientFirstName',
      'recipientLastName',
      'registrationLanguage',
      'deliveryDate',
      'expiresAt',
      'tokenURL',
      'message',
      'planName',
      'token',
    ],
    workerRequiredFieldKeys: ['delivery_date', 'expires_at', 'plan_name', 'token', 'token_url'],
    notes: 'POST body uses gift schema (camelCase root); worker maps to snake_case context.',
  },
  GIFT_SUBSCRIPTION_PURCHASER_RECEIPT: {
    eventName: 'GIFT_SUBSCRIPTION_PURCHASER_RECEIPT',
    typicalBrazeContextKeys: [
      'event_name',
      'registration_language',
      'first_name',
      'last_name',
      'plan_name',
      'country',
      'currency',
    ],
    adminFieldKeys: [
      'first_name',
      'last_name',
      'registration_language',
      'plan_name',
      'country',
      'currency',
    ],
    workerRequiredFieldKeys: [],
    notes: 'Worker merges payload root planName from plan_name when sent.',
  },
  GIFT_SUBSCRIPTION_REDEEMED: {
    eventName: 'GIFT_SUBSCRIPTION_REDEEMED',
    typicalBrazeContextKeys: ['event_name', 'registration_language', 'first_name'],
    adminFieldKeys: ['first_name', 'registration_language'],
    workerRequiredFieldKeys: [],
  },
  SHARE_VIDEO_LEAD: {
    eventName: 'SHARE_VIDEO_LEAD',
    typicalBrazeContextKeys: [
      'event_name',
      'form_name',
      'registration_language',
      'shared_video_id',
      'url',
      'new_share_url',
      'share_watch_link_clicked',
      'prospect_behavior_segment',
      'prospect_email_comm_br',
    ],
    adminFieldKeys: [
      'form_name',
      'registration_language',
      'shared_video_id',
      'url',
      'new_share_url',
      'share_watch_link_clicked',
      'prospect_behavior_segment',
      'prospect_email_comm_br',
      'everflowTransactionId',
      'utm',
    ],
    workerRequiredFieldKeys: ['form_name', 'url', 'shared_video_id'],
    notes: 'everflowTransactionId and utm are payload root keys (merged into fields by worker).',
  },
  ALACARTE_PURCHASE_MADE: {
    eventName: 'ALACARTE_PURCHASE_MADE',
    typicalBrazeContextKeys: [
      'event_name',
      'registration_language',
      'is_new_user',
      'password_reset_url',
      'includes_content',
      'entitled_ecommerce_sku_keys',
    ],
    adminFieldKeys: [
      'registration_language',
      'language',
      'first_name',
      'is_new_user',
      'password_reset_url',
      'includes_content',
      'entitled_ecommerce_sku_keys',
    ],
    workerRequiredFieldKeys: [],
    notes: 'language maps to registration_language on profile; omitted from Canvas context.',
  },
  BONUS_GRANTED: {
    eventName: 'BONUS_GRANTED',
    typicalBrazeContextKeys: [
      'event_name',
      'language',
      'includes_content',
      'is_new_user',
      'entitled_ecommerce_sku_keys',
      'ecommerce_sku_mapping_bonus_eligibilities',
    ],
    adminFieldKeys: [
      'registration_language',
      'language',
      'plan_name',
      'first_name',
      'includes_content',
      'is_new_user',
      'entitled_ecommerce_sku_keys',
      'ecommerce_sku_mapping_bonus_eligibilities',
    ],
    workerRequiredFieldKeys: [],
  },
  BONUS_SKU_SUBSCRIPTION_SIGNUP: {
    eventName: 'BONUS_SKU_SUBSCRIPTION_SIGNUP',
    typicalBrazeContextKeys: [
      'event_name',
      'language',
      'plan_name',
      'subscription_tier',
      'trial_period',
      'gaia_product_sku',
      'next_bill_amount',
      'paid_through_date',
    ],
    adminFieldKeys: [
      'registration_language',
      'language',
      'plan_name',
      'subscription_tier',
      'trial_period',
      'gaia_product_sku',
      'next_bill_amount',
      'paid_through_date',
      'currency',
    ],
    workerRequiredFieldKeys: [],
  },
  BONUS_SKU_SUBSCRIPTION_PLAN_CHANGE: {
    eventName: 'BONUS_SKU_SUBSCRIPTION_PLAN_CHANGE',
    typicalBrazeContextKeys: [
      'event_name',
      'language',
      'plan_name',
      'subscription_tier',
      'trial_period',
      'gaia_product_sku',
      'next_bill_amount',
      'paid_through_date',
      'includes_content',
      'currency',
      'creation_source',
    ],
    adminFieldKeys: [
      'registration_language',
      'language',
      'plan_name',
      'subscription_tier',
      'trial_period',
      'gaia_product_sku',
      'next_bill_amount',
      'paid_through_date',
      'currency',
      'includes_content',
      'creation_source',
      'entitled_ecommerce_sku_keys',
      'ecommerce_sku_mapping_bonus_eligibilities',
    ],
    workerRequiredFieldKeys: [],
  },
}

export function getTypicalBrazeContextKeys (templateId: string): string[] {
  const entry = CUSTOM_EVENT_TRIGGER_FIELD_CATALOG[templateId]
  return entry ? [...entry.typicalBrazeContextKeys] : []
}

export function getCustomEventCatalogNotes (templateId: string): string | undefined {
  return CUSTOM_EVENT_TRIGGER_FIELD_CATALOG[templateId]?.notes
}

const CUSTOM_EVENT_SEND_HINTS: Record<string, string> = {
  PASSWORD_RESET: 'Include a valid reset link and language so the email renders correctly.',
  NEW_USER_PASSWORD_RESET:
    'Include a valid reset link and language for the new-member campaign email.',
  LOGIN_CODE: 'For code-and-link emails, fill in the magic link and the numeric code.',
  GIFT_SUBSCRIPTION_RECIPIENT_DELIVERY:
    'Include delivery dates, plan name, and the gift link so the recipient email is complete.',
  GIFT_SUBSCRIPTION_PURCHASER_RECEIPT:
    'Include purchaser name, plan, and currency for the receipt email.',
  GIFT_SUBSCRIPTION_REDEEMED: 'Include language and first name for the redemption confirmation.',
  SHARE_VIDEO_LEAD: 'Include the share form name, video link, and video id for the lead email.',
  ALACARTE_PURCHASE_MADE:
    'Include language and entitled product SKUs for the purchase confirmation.',
  BONUS_GRANTED: 'Include bonus SKU rows and entitled products so the grant email is complete.',
  BONUS_SKU_SUBSCRIPTION_SIGNUP:
    'Include plan, tier, trial, and billing fields for the signup email.',
  BONUS_SKU_SUBSCRIPTION_PLAN_CHANGE:
    'Include plan, tier, and billing fields for the plan change email.',
}

const CUSTOM_EVENT_SEND_HINT_FALLBACK =
  'Fill the recommended fields below for a complete test send.'

/** Plain-language guidance for the transactional template info tooltip (Admin QA). */
export function getCustomEventSendHint (templateId: string): string {
  return CUSTOM_EVENT_SEND_HINTS[templateId] ?? CUSTOM_EVENT_SEND_HINT_FALLBACK
}
